import { useRef, useState } from "react";
import { SEVERITY_COLORS } from "../constants";
import { btn, card, cardHead, primaryBtn, t, tabular } from "../styles";
import { fmtDate, money } from "../utils";

const ACCEPT = ".pdf,.docx,.doc";

const FileBadge = ({ name }) => {
  const ext = (name.split(".").pop() || "").toUpperCase().slice(0, 4);
  const isPdf = ext === "PDF";
  return (
    <div
      style={{
        width: 36,
        height: 36,
        borderRadius: 9,
        background: isPdf ? "#f7dedc" : "#e8f1fb",
        color: isPdf ? "#a4302e" : "#1c5b8f",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 10,
        fontWeight: 700,
        flex: "none",
      }}
    >
      {ext}
    </div>
  );
};

const Row = ({ columns, children }) => (
  <div
    style={{
      display: "grid",
      gridTemplateColumns: columns,
      gap: 12,
      alignItems: "center",
      padding: "10px 14px",
      borderBottom: "1px solid #f4f4f7",
    }}
  >
    {children}
  </div>
);

const issueText = (k) => `[${k.severity}] ${k.title}${k.detail ? `\n${k.detail}` : ""}${k.where ? `\n(${k.where})` : ""}`;

const ContractReview = ({ findings, pvc }) => {
  const [copied, setCopied] = useState("");
  const high = findings.filter((k) => k.severity === "High").length;
  const tone = high
    ? { fg: "#a4302e", bg: "#fdf4f3", border: "#f0c9c7" }
    : findings.length
    ? { fg: "#8a5a0c", bg: "#fffbf1", border: "#f2dcae" }
    : { fg: t.success, bg: "#f3faf6", border: "#cfe8da" };

  const copy = (text, key) => {
    try {
      navigator.clipboard.writeText(text);
    } catch (e) {
      // Clipboard can be unavailable (insecure context); nothing to do.
    }
    setCopied(key);
    setTimeout(() => setCopied(""), 1800);
  };

  const summary = findings.length
    ? `${findings.length} issue${findings.length === 1 ? "" : "s"} found in the document${high ? ` · ${high} high` : ""} · copy them to send to whoever drafted the contract`
    : "No issues found";

  return (
    <div style={{ border: `1px solid ${tone.border}`, borderRadius: 12, overflow: "hidden" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          flexWrap: "wrap",
          padding: "12px 14px",
          background: tone.bg,
          borderBottom: findings.length ? `1px solid ${tone.border}` : "none",
        }}
      >
        <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.8, textTransform: "uppercase", color: t.textSecondary }}>
          Contract review
        </span>
        <span style={{ fontSize: 12.5, fontWeight: 600, color: tone.fg, flex: 1, minWidth: 180 }}>{summary}</span>
        {findings.length > 0 && (
          <button
            type="button"
            className="cw2-btn"
            onClick={() =>
              copy(
                `Issues found in contract${pvc ? ` PVC–${pvc}` : ""}:\n\n` +
                  findings.map((k, i) => `${i + 1}. ${issueText(k)}`).join("\n\n"),
                "all"
              )
            }
            style={{ ...btn, fontSize: 12, padding: "6px 11px", borderRadius: 7, color: copied === "all" ? t.success : t.text, whiteSpace: "nowrap" }}
          >
            {copied === "all" ? "Copied all" : "Copy all"}
          </button>
        )}
      </div>
      {findings.map((k, i) => {
        const [bg, fg] = SEVERITY_COLORS[k.severity] || SEVERITY_COLORS.Low;
        const key = `f${i}`;
        return (
          <div
            key={key}
            style={{
              display: "grid",
              gridTemplateColumns: "auto minmax(0,1fr) auto",
              gap: 12,
              alignItems: "start",
              padding: "12px 14px",
              borderBottom: "1px solid #f4f4f7",
            }}
          >
            <span
              style={{
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: 0.4,
                textTransform: "uppercase",
                padding: "3px 7px",
                borderRadius: 5,
                background: bg,
                color: fg,
                marginTop: 1,
                minWidth: 62,
                textAlign: "center",
              }}
            >
              {k.severity}
            </span>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 13.5, fontWeight: 650 }}>{k.title}</div>
              {k.detail && <div style={{ fontSize: 12.5, color: "#3c3c46", marginTop: 3, lineHeight: 1.5 }}>{k.detail}</div>}
              {k.where && <div style={{ fontSize: 11.5, color: "#6f6f7c", marginTop: 4 }}>{k.where}</div>}
            </div>
            <button
              type="button"
              className="cw2-btn"
              title="Copy this issue"
              onClick={() => copy(issueText(k), key)}
              style={{ ...btn, fontSize: 12, padding: "6px 10px", borderRadius: 7, color: copied === key ? t.success : t.text, whiteSpace: "nowrap" }}
            >
              {copied === key ? "Copied" : "Copy"}
            </button>
          </div>
        );
      })}
    </div>
  );
};

/**
 * Step 1: optional. Upload the signed contract; the backend extracts what it
 * can and the wizard pre-fills the next steps with it.
 */
const ImportStep = ({ importState, importError, file, extracted, onPick, onReplace, onSkip }) => {
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);

  const pick = (f) => {
    if (f) onPick(f);
  };

  const hiddenInput = (
    <input
      ref={inputRef}
      type="file"
      accept={ACCEPT}
      style={{ display: "none" }}
      onChange={(e) => {
        pick(e.target.files?.[0]);
        e.target.value = "";
      }}
    />
  );

  if (importState === "idle" || importState === "error") {
    return (
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          pick(e.dataTransfer.files?.[0]);
        }}
        style={{
          border: `1.5px dashed ${dragOver ? t.accent : "#c9c6f0"}`,
          borderRadius: 14,
          background: dragOver ? "#f0eefd" : "#f8f7fe",
          padding: "48px 24px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          gap: 10,
        }}
      >
        {hiddenInput}
        <div
          style={{
            width: 44,
            height: 44,
            borderRadius: 12,
            background: "#eceafd",
            color: t.accent,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 16V4" />
            <path d="M7 9l5-5 5 5" />
            <path d="M4 20h16" />
          </svg>
        </div>
        <div style={{ fontSize: 15, fontWeight: 650 }}>Drop the signed contract here</div>
        <div style={{ fontSize: 13, color: t.textSecondary, maxWidth: 420, lineHeight: 1.5 }}>
          PDF or Word document. We read the PVC number, client, units, prices and payments and pre-fill the next steps.
        </div>
        {importState === "error" && (
          <div style={{ fontSize: 12.5, color: t.dangerText, maxWidth: 460, lineHeight: 1.5 }}>
            {importError || "Couldn't read that document."} Try another file, or enter the contract manually.
          </div>
        )}
        <div style={{ display: "flex", gap: 9, marginTop: 6, flexWrap: "wrap", justifyContent: "center" }}>
          <button type="button" className="cw2-primary" onClick={() => inputRef.current?.click()} style={primaryBtn}>
            Choose file
          </button>
          <button type="button" className="cw2-btn" onClick={onSkip} style={btn}>
            Enter manually
          </button>
        </div>
      </div>
    );
  }

  if (importState === "reading") {
    return (
      <div style={{ border: `1px solid ${t.border}`, borderRadius: 14, padding: 20, display: "flex", flexDirection: "column", gap: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <FileBadge name={file?.name || ""} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13.5, fontWeight: 600 }}>{file?.name}</div>
            <div style={{ fontSize: 12, color: "#6f6f7c", marginTop: 2 }}>Reading document and extracting contract data…</div>
          </div>
        </div>
        <div style={{ height: 5, borderRadius: 3, background: "#eceaf1", overflow: "hidden" }}>
          <div className="progress-bar progress-bar-striped progress-bar-animated" style={{ height: "100%", width: "100%", background: "#6366f1" }} />
        </div>
      </div>
    );
  }

  // done
  const { fields, units, payments, findings, summary, pvc } = extracted;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      {hiddenInput}
      <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", border: `1px solid ${t.border}`, borderRadius: 12, flexWrap: "wrap" }}>
        <FileBadge name={file?.name || ""} />
        <div style={{ flex: 1, minWidth: 180 }}>
          <div style={{ fontSize: 13.5, fontWeight: 600 }}>{file?.name}</div>
          <div style={{ fontSize: 12, color: t.success, marginTop: 2 }}>{summary}</div>
        </div>
        <button type="button" className="cw2-btn" onClick={onReplace} style={{ ...btn, fontSize: 12.5, padding: "7px 12px", borderRadius: 8 }}>
          Replace
        </button>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(300px,1fr))", gap: 16, alignItems: "start" }}>
        <div style={card}>
          <div style={cardHead}>Contract details</div>
          {fields.map((f) => (
            <Row key={f.label} columns="120px minmax(0,1fr) auto">
              <span style={{ fontSize: 12.5, color: "#6f6f7c" }}>{f.label}</span>
              <span style={{ fontSize: 13.5, fontWeight: 500, color: f.value ? t.text : "#8a6b12", overflowWrap: "anywhere" }}>
                {f.value || "Not found in document"}
              </span>
              {!f.value && (
                <span style={{ fontSize: 10, fontWeight: 650, letterSpacing: 0.4, textTransform: "uppercase", padding: "2px 7px", borderRadius: 5, background: "#fdf7e7", color: "#8a6b12" }}>
                  Fill in
                </span>
              )}
            </Row>
          ))}
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div style={card}>
            <div style={cardHead}>Units</div>
            {units.length === 0 && <div style={{ padding: "12px 14px", fontSize: 12.5, color: "#6f6f7c" }}>No units matched in the inventory</div>}
            {units.map((u) => (
              <Row key={u.id} columns="minmax(0,1fr) auto">
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 13.5, fontWeight: 600 }}>{u.label}</div>
                  <div style={{ fontSize: 11.5, color: "#6f6f7c", marginTop: 2 }}>{u.path}</div>
                </div>
                <div style={{ fontSize: 13.5, fontWeight: 600, ...tabular }}>{money(u.settle)}</div>
              </Row>
            ))}
          </div>
          <div style={card}>
            <div style={cardHead}>Payments</div>
            {payments.length === 0 && <div style={{ padding: "12px 14px", fontSize: 12.5, color: "#6f6f7c" }}>No payments found</div>}
            {payments.map((p, i) => (
              <Row key={i} columns="minmax(0,1fr) auto">
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 13.5, fontWeight: 600 }}>{p.label}</div>
                  <div style={{ fontSize: 11.5, color: "#6f6f7c", marginTop: 2 }}>{p.date ? `Due ${fmtDate(p.date)}` : "No date"}</div>
                </div>
                <span style={{ fontSize: 13.5, fontWeight: 600, ...tabular }}>{money(p.amount)}</span>
              </Row>
            ))}
          </div>
        </div>
      </div>

      <ContractReview findings={findings} pvc={pvc} />

      <div style={{ fontSize: 12.5, color: t.textSecondary }}>
        These values are pre-filled in the next steps. Anything marked{" "}
        <span style={{ fontWeight: 650, color: "#8a6b12" }}>Fill in</span> wasn't found in the document — add it in the next step.
      </div>
    </div>
  );
};

export default ImportStep;
